import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedDatePicker from '../ui/LocalizedDatePicker';
import { DocumentAllowance, Skonto } from '../../types/invoiceTypes';
import { TAX_CATEGORY_KEYS, type InvoiceTaxCategoryKey } from '../../utils/taxCategoryCatalog';
import { getPeriodError } from '../../utils/billingPeriodValidation';

// VB-1 Feature-Flag (build-time, fail-closed, Default AUS).
const TAX_CATEGORY_ENABLED = process.env.REACT_APP_TAX_CATEGORY_ENABLED === 'true';

// Props-Interface für die optionalen Felder
interface OptionalFieldsFormProps {
  paymentTerms: string | null;
  setPaymentTerms: (value: string | null) => void;
  notes: string | null;
  setNotes: (value: string | null) => void;
  billingStartDate: string | null;
  setBillingStartDate: (value: string | null) => void;
  billingEndDate: string | null;
  setBillingEndDate: (value: string | null) => void;
  tenderOrLotReference: string | null;
  setTenderOrLotReference: (value: string | null) => void;
  invoicedObjectId: string | null;
  setInvoicedObjectId: (value: string | null) => void;
  invoicedObjectScheme: string | null;
  setInvoicedObjectScheme: (value: string | null) => void;
  contractReference: string | null;
  setContractReference: (value: string | null) => void;
  projectReferenceId: string | null;
  setProjectReferenceId: (value: string | null) => void;
  projectReferenceName: string | null;
  setProjectReferenceName: (value: string | null) => void;
  paymentDueDate: string | null;
  setPaymentDueDate: (value: string | null) => void;
  paymentMandateReference: string | null;
  setPaymentMandateReference: (value: string | null) => void;
  buyerOrderReference: string | null;
  setBuyerOrderReference: (value: string | null) => void;
  sellerOrderReference: string | null;
  setSellerOrderReference: (value: string | null) => void;
  // Neue Felder: Dokumentrabatte und Skonto
  documentAllowances?: DocumentAllowance[];
  setDocumentAllowances?: (allowances: DocumentAllowance[]) => void;
  skonto?: Skonto | null;
  setSkonto?: (skonto: Skonto | null) => void;
  // VB-1: rechnungsweiter Steuerart-Default für neue Positionen (nur bei aktivem Flag).
  defaultTaxCategorySelection?: InvoiceTaxCategoryKey | null;
  setDefaultTaxCategorySelection?: (value: InvoiceTaxCategoryKey) => void;
  // Email-Versand
}

const OptionalFieldsForm: React.FC<OptionalFieldsFormProps> = (props) => {
  const { t } = useTranslation(['invoice', 'common']);

  const TAX_RATES = [0, 7, 19];

  const addAllowance = () => {
    const newAllowance: DocumentAllowance = { isCharge: false, type: 'percentage', value: 0, taxRate: 19 };
    props.setDocumentAllowances?.([...(props.documentAllowances ?? []), newAllowance]);
  };

  const updateAllowance = (idx: number, patch: Partial<DocumentAllowance>) => {
    const updated = (props.documentAllowances ?? []).map((a, i) => i === idx ? { ...a, ...patch } : a);
    props.setDocumentAllowances?.(updated);
  };

  const removeAllowance = (idx: number) => {
    props.setDocumentAllowances?.((props.documentAllowances ?? []).filter((_, i) => i !== idx));
  };

  // A-6 (Re-Loop-Auflage): Beides-oder-keins- und Reihenfolge-Validierung für
  // den Leistungszeitraum. Rein darstellend — OptionalFieldsForm hat keine
  // Submit-Logik, blockiert also das Absenden nicht, macht den Fehler aber
  // sichtbar bevor die Rechnung erzeugt wird. Die eigentliche Regel steckt in
  // `getPeriodError` (utils/billingPeriodValidation.ts) — einzige Quelle der
  // Wahrheit, dieselbe Funktion wertet auch der Abschnittskopf
  // (CollapsibleSection/MobileCollapsibleSection `error`-Prop) in den drei
  // Masken aus (🟡-2, review-report-leistungszeitraum-mobil.md), damit der
  // Fehler auch im zugeklappten Zustand sichtbar bleibt.
  const periodError = useMemo(
    () => getPeriodError(props.billingStartDate, props.billingEndDate),
    [props.billingStartDate, props.billingEndDate]
  );

  return (
    <>
      {/* --- Gruppe: Zahlung --- */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-accent-ink mb-3">{t('advanced.payment')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Zahlungsbedingungen (volle Breite) */}
          <div className="md:col-span-2">
            <label htmlFor="paymentTerms" className="block text-sm font-medium text-secondary mb-1">
              {t('advanced.paymentTerms')}
            </label>
            <input
              type="text"
              id="paymentTerms"
              value={props.paymentTerms || ''}
              onChange={(e) => props.setPaymentTerms(e.target.value || null)}
              className="input-field placeholder-subtle"
              placeholder="z.B. Zahlbar innerhalb 14 Tagen ohne Abzug"
            />
          </div>

          {/* Fälligkeitsdatum */}
          <div>
            <label htmlFor="paymentDueDate" className="block text-sm font-medium text-secondary mb-1">{t('advanced.paymentDueDate')}</label>
            <LocalizedDatePicker
              id="paymentDueDate"
              name="paymentDueDate"
              value={props.paymentDueDate || ''}
              onChange={(value) => props.setPaymentDueDate(value || null)}
              placeholder={t('advanced.paymentDueDate')}
            />
          </div>

          {/* Mandatsreferenz */}
          <div>
            <label htmlFor="paymentMandateReference" className="block text-sm font-medium text-secondary mb-1">{t('advanced.mandateReference')}</label>
            <input
              type="text"
              id="paymentMandateReference"
              value={props.paymentMandateReference || ''}
              onChange={(e) => props.setPaymentMandateReference(e.target.value || null)}
              className="input-field placeholder-subtle"
              placeholder="Mandat123"
            />
          </div>
        </div>

        {/* --- Skonto --- */}
        {props.setSkonto && (
          <div className="mt-4 border-t border-hairline pt-4">
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                id="skontoToggle"
                checked={!!props.skonto}
                onChange={(e) => {
                  if (e.target.checked) {
                    props.setSkonto?.({ percentage: 2, days: 14 });
                  } else {
                    props.setSkonto?.(null);
                  }
                }}
                className="w-4 h-4 accent-indigo-500"
              />
              <label htmlFor="skontoToggle" className="text-sm font-medium text-secondary">
                {t('skonto.toggle', { ns: 'common' })}
              </label>
            </div>
            {props.skonto && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 pl-6">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    {t('skonto.percent', { ns: 'common' })}
                  </label>
                  <input
                    type="number"
                    min={0.01}
                    max={100}
                    step={0.1}
                    value={props.skonto.percentage}
                    onChange={(e) => props.setSkonto?.({ ...props.skonto!, percentage: parseFloat(e.target.value) || 0 })}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    {t('skonto.days', { ns: 'common' })}
                  </label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={props.skonto.days}
                    onChange={(e) => props.setSkonto?.({ ...props.skonto!, days: parseInt(e.target.value) || 1 })}
                    className="input-field"
                  />
                </div>
                <div className="md:col-span-2">
                  <p className="text-xs text-accent-ink italic">
                    {t('skonto.preview', { ns: 'common', percent: props.skonto.percentage, days: props.skonto.days })}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- Gruppe: Dokumentrabatt / Aufschlag --- */}
      {props.setDocumentAllowances && (
        <div className="mb-6 border-t border-hairline pt-4 mt-4">
          <h3 className="text-lg font-medium text-accent-ink mb-3">
            {t('documentAllowances.title', { ns: 'common' })}
          </h3>
          {(props.documentAllowances ?? []).map((ac, idx) => (
            <div key={idx} className="border border-strong rounded-md p-3 mb-3 bg-surface-inset grid grid-cols-2 md:grid-cols-4 gap-3 relative">
              {/* isCharge Toggle */}
              <div>
                <label className="block text-xs text-muted mb-1">Typ</label>
                <select
                  value={ac.isCharge ? 'charge' : 'discount'}
                  onChange={(e) => updateAllowance(idx, { isCharge: e.target.value === 'charge' })}
                  className="input-field text-sm"
                >
                  <option value="discount">{t('documentAllowances.discount', { ns: 'common' })}</option>
                  <option value="charge">{t('documentAllowances.charge', { ns: 'common' })}</option>
                </select>
              </div>
              {/* Wert-Typ */}
              <div>
                <label className="block text-xs text-muted mb-1">{t('documentAllowances.byPercent', { ns: 'common' })}/{t('documentAllowances.byAmount', { ns: 'common' })}</label>
                <select
                  value={ac.type}
                  onChange={(e) => updateAllowance(idx, { type: e.target.value as 'percentage' | 'amount' })}
                  className="input-field text-sm"
                >
                  <option value="percentage">{t('documentAllowances.byPercent', { ns: 'common' })}</option>
                  <option value="amount">{t('documentAllowances.byAmount', { ns: 'common' })}</option>
                </select>
              </div>
              {/* Wert */}
              <div>
                <label className="block text-xs text-muted mb-1">
                  {ac.type === 'percentage' ? t('documentAllowances.byPercent', { ns: 'common' }) : t('documentAllowances.byAmount', { ns: 'common' })}
                </label>
                <input
                  type="number"
                  min={0}
                  step={ac.type === 'percentage' ? 0.1 : 0.01}
                  value={ac.value}
                  onChange={(e) => updateAllowance(idx, { value: parseFloat(e.target.value) || 0 })}
                  className="input-field text-sm"
                />
              </div>
              {/* MwSt-Satz */}
              <div>
                <label className="block text-xs text-muted mb-1">{t('documentAllowances.taxRate', { ns: 'common' })}</label>
                <select
                  value={ac.taxRate}
                  onChange={(e) => updateAllowance(idx, { taxRate: parseFloat(e.target.value) })}
                  className="input-field text-sm"
                >
                  {TAX_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                </select>
              </div>
              {/* Bezeichnung */}
              <div className="col-span-2 md:col-span-3">
                <label className="block text-xs text-muted mb-1">{t('documentAllowances.reason', { ns: 'common' })}</label>
                <input
                  type="text"
                  value={ac.reason ?? ''}
                  onChange={(e) => updateAllowance(idx, { reason: e.target.value || undefined })}
                  className="input-field text-sm placeholder-subtle"
                  placeholder="z.B. Treuerabatt"
                />
              </div>
              {/* Entfernen */}
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => removeAllowance(idx)}
                  className="btn-danger-sm w-full"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addAllowance}
            className="btn-secondary-sm mt-1"
          >
            {t('documentAllowances.add', { ns: 'common' })}
          </button>
        </div>
      )}

      {/* --- VB-1: Steuerart-Default (rechnungsweit) --- */}
      {TAX_CATEGORY_ENABLED && props.setDefaultTaxCategorySelection && (
        <div className="mb-6 border-t border-hairline pt-4 mt-4">
          <h3 className="text-lg font-medium text-accent-ink mb-3">{t('taxCategory.defaultTitle')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="defaultTaxCategory" className="block text-sm font-medium text-secondary mb-1">
                {t('taxCategory.label')}
              </label>
              <select
                id="defaultTaxCategory"
                value={props.defaultTaxCategorySelection || 'standard'}
                onChange={(e) => props.setDefaultTaxCategorySelection?.(e.target.value as InvoiceTaxCategoryKey)}
                className="input-field"
              >
                {TAX_CATEGORY_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {t(`taxCategory.options.${key}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <p className="text-xs text-muted">{t('taxCategory.defaultHint')}</p>
            </div>
          </div>
        </div>
      )}

      {/* --- Gruppe: Referenzen --- */}
      <div className="mb-6 border-t border-hairline pt-4 mt-4">
        <h3 className="text-lg font-medium text-accent-ink mb-3">{t('advanced.references')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Bestellreferenz Käufer */}
          <div>
            <label htmlFor="buyerOrderReference" className="block text-sm font-medium text-secondary mb-1">{t('advanced.buyerOrderRef')}</label>
            <input
              type="text"
              id="buyerOrderReference"
              value={props.buyerOrderReference || ''}
              onChange={(e) => props.setBuyerOrderReference(e.target.value || null)}
              className="input-field placeholder-subtle"
                              placeholder={t('advanced.buyerOrderRefPlaceholder')}
            />
          </div>

          {/* Auftragsreferenz Verkäufer */}
          <div>
            <label htmlFor="sellerOrderReference" className="block text-sm font-medium text-secondary mb-1">{t('advanced.sellerOrderRef')}</label>
            <input
              type="text"
              id="sellerOrderReference"
              value={props.sellerOrderReference || ''}
              onChange={(e) => props.setSellerOrderReference(e.target.value || null)}
              className="input-field placeholder-subtle"
                              placeholder={t('advanced.sellerOrderRefPlaceholder')}
            />
          </div>

          {/* Vertragsreferenz */}
          <div>
            <label htmlFor="contractReference" className="block text-sm font-medium text-secondary mb-1">{t('advanced.contractRef')}</label>
            <input
              type="text"
              id="contractReference"
              value={props.contractReference || ''}
              onChange={(e) => props.setContractReference(e.target.value || null)}
              className="input-field placeholder-subtle"
                              placeholder={t('advanced.contractRefPlaceholder')}
            />
          </div>

          {/* Projektreferenz ID */}
          <div>
            <label htmlFor="projectReferenceId" className="block text-sm font-medium text-secondary mb-1">{t('advanced.projectRefId')}</label>
            <input
              type="text"
              id="projectReferenceId"
              value={props.projectReferenceId || ''}
              onChange={(e) => props.setProjectReferenceId(e.target.value || null)}
              className="input-field placeholder-subtle"
                              placeholder={t('advanced.projectRefIdPlaceholder')}
            />
          </div>

          {/* Projektreferenz Name */}
          <div>
            <label htmlFor="projectReferenceName" className="block text-sm font-medium text-secondary mb-1">{t('advanced.projectRefName')}</label>
            <input
              type="text"
              id="projectReferenceName"
              value={props.projectReferenceName || ''}
              onChange={(e) => props.setProjectReferenceName(e.target.value || null)}
              className="input-field placeholder-subtle"
                              placeholder={t('advanced.projectRefNamePlaceholder')}
            />
          </div>

          {/* Ausschreibungs-/Losreferenz */}
          <div>
            <label htmlFor="tenderOrLotReference" className="block text-sm font-medium text-secondary mb-1">{t('advanced.tenderRef')}</label>
            <input
              type="text"
              id="tenderOrLotReference"
              value={props.tenderOrLotReference || ''}
              onChange={(e) => props.setTenderOrLotReference(e.target.value || null)}
              className="input-field placeholder-subtle"
                              placeholder={t('advanced.tenderRefPlaceholder')}
            />
          </div>

          {/* Objektkennung ID */}
          <div>
            <label htmlFor="invoicedObjectId" className="block text-sm font-medium text-secondary mb-1">{t('advanced.objectId')}</label>
            <input
              type="text"
              id="invoicedObjectId"
              value={props.invoicedObjectId || ''}
              onChange={(e) => props.setInvoicedObjectId(e.target.value || null)}
              className="input-field placeholder-subtle"
                              placeholder={t('advanced.objectIdPlaceholder')}
            />
          </div>

          {/* Objektkennung Schema */}
          <div>
            <label htmlFor="invoicedObjectScheme" className="block text-sm font-medium text-secondary mb-1">{t('advanced.objectScheme')}</label>
            <input
              type="text"
              id="invoicedObjectScheme"
              value={props.invoicedObjectScheme || ''}
              onChange={(e) => props.setInvoicedObjectScheme(e.target.value || null)}
              className="input-field placeholder-subtle"
                              placeholder={t('advanced.objectSchemePlaceholder')}
            />
          </div>
        </div>
      </div>

      {/* --- Gruppe: Abrechnungszeitraum --- */}
      <div className="mb-6 border-t border-hairline pt-4 mt-4">
        <h3 className="text-lg font-medium text-accent-ink mb-3">{t('advanced.billingPeriod')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Abrechnungszeitraum Start */}
          <div>
            <label htmlFor="billingStartDate" className="block text-sm font-medium text-secondary mb-1">{t('advanced.periodStart')}</label>
            <LocalizedDatePicker
              id="billingStartDate"
              name="billingStartDate"
              value={props.billingStartDate || ''}
              onChange={(value) => props.setBillingStartDate(value || null)}
              placeholder={t('advanced.periodStart')}
              className={periodError ? 'input-field-error' : undefined}
            />
          </div>

          {/* Abrechnungszeitraum Ende */}
          <div>
            <label htmlFor="billingEndDate" className="block text-sm font-medium text-secondary mb-1">{t('advanced.periodEnd')}</label>
            <LocalizedDatePicker
              id="billingEndDate"
              name="billingEndDate"
              value={props.billingEndDate || ''}
              onChange={(value) => props.setBillingEndDate(value || null)}
              placeholder={t('advanced.periodEnd')}
              className={periodError ? 'input-field-error' : undefined}
            />
          </div>

          {periodError && (
            <p className="md:col-span-2 mt-1 text-sm text-red-500" role="alert">
              {periodError === 'incomplete' ? t('advanced.periodIncompleteError') : t('advanced.periodOrderError')}
            </p>
          )}
        </div>
      </div>

      {/* --- Gruppe: Notizen --- */}
      <div className="border-t border-hairline pt-4 mt-4">
        <h3 className="text-lg font-medium text-accent-ink mb-3">{t('advanced.notes')}</h3>
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-secondary mb-1">
                          {t('advanced.notesDescription')}
          </label>
          <textarea
            id="notes"
            rows={3}
            value={props.notes || ''}
            onChange={(e) => props.setNotes(e.target.value || null)}
            className="textarea-field placeholder-subtle"
            placeholder="Zusätzliche Informationen oder Anmerkungen..."
          />
        </div>
      </div>

       {/* Input-Field Styling: Statt input-field direkt Tailwind Klassen verwenden */}
       {/* Beispiel für ein input-field Äquivalent (kann global definiert werden): */}
       {/* className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" */}

    </>
  );
};

export default OptionalFieldsForm; 